import { makeStyles } from "tss-react/mui"
import { Link, useParams } from "react-router-dom"
import { Box, Stack, Typography } from "@mui/material"
import { useApiQuery, usePublicApiQuery } from "../../../../core/api/useApiQuery"
import { fetchOrderDetails, fetchPublicOrderDetails, OrderDetailsResponse } from "../home/meetingServiceApi"
import { UserWeekMenu } from "../home/weekMenuApi"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { ReceiptDetailsSkeleton } from "./ReceiptDetailsSkeleton"
import { SharePrintButtons } from "./components/SharePrintButtons"
import { useMemo, useState } from "react"
import { useAppSelector } from "../../../store/storeHooks"
import { getPublicMeetingReceiptRoute, LOGIN_ROUTE } from "../../../../constants/routes"
import { MenuMeeting } from "../home/weekMenuTypes"
import {
    selectEffectiveCompanyId,
    selectEffectiveUserId,
    selectEffectiveUserType,
} from "../../../context/contextSelectors"
import { UserType as LoginUserType } from "../../../login/loginTypes"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { MeetingServiceOrder as MeetingServiceOrderAccounting } from "../../../merchant/accounting/accounting/accountingTypes"
import { MeetingServiceOrder } from "../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { StandardDialog } from "../../../../shared/components/dialogs/StandardDialog"
import FacilitynetLogo from "../../../../shared/layouts/assets/facilit-net-logo.svg"
import { MeetingDetailsOrder } from "../../../serviceCenter/report/home/meetings/meetingTypes"
import {
    fetchMeetingServiceReceiptOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../../../merchant/merchant/production/meetingService/meetingServiceApi"
import { useMerchant } from "../../../merchants/merchantHooks"
import { ReceiptDetails } from "./ReceiptDetails"
import { ServiceCenterBooking } from "../../../serviceCenter/report/home/serviceCenterHomeTypes"
import { MeetingCardOrderTypes } from "../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { useEnvironment } from "../../../environment/envHooks"
import { ReceiptAndSlipDialogHeader } from "../../../merchant/merchant/components/receiptAndSlip/ReceiptAndSlipDialogHeader"
import { ReceiptNavigation } from "../../../../shared/components/receipt/ReceiptNavigation"
import { OrderChatHistoryPage } from "../../../../shared/components/orderChat/print/OrderChatHistoryPage"
import { OrderChatStatusCard } from "../../../../shared/components/orderChat/OrderChatStatusCard"
import { OrderChatDrawer } from "../../../../shared/components/orderChat/OrderChatDrawer"
import {
    ChatMessageAuthorRole,
    ChatMessageSender,
    UserType,
} from "../../../../shared/components/orderChat/orderChatTypes"
import type { ChatOrder } from "../../../../shared/components/orderChat/orderChatTypes"
import { HIDDEN_IN_PRINT, VISIBLE_ONLY_IN_PRINT } from "../../../../shared/components/print/printPageOnly"
import { PrintOrderPage } from "../../../../shared/components/print/PrintOrderPage"
import { PrintPageHeaderFooter } from "../../../../shared/components/print/PrintPageHeaderFooter"
import { getKitchenNames } from "../../../merchant/merchant/production/meetingService/components/slipModal/utils"
import { usePrintWithChatHistory } from "../../../../shared/components/orderChat/print/usePrintWithChatHistory"
import type { RenderPrintViewOrderMenu } from "../../../merchant/merchant/components/receiptAndSlip/PrintViewOrderActions"

function getChatSender(
    viewerUserType: LoginUserType | undefined,
    userId: number,
    companyId: number | undefined,
): ChatMessageSender | undefined {
    if (!userId || !companyId || viewerUserType === LoginUserType.SUPPORT) return undefined
    if (viewerUserType === LoginUserType.MERCHANT) return { type: UserType.MERCHANT, merchantId: companyId, userId }
    return { type: UserType.USER, companyId, userId }
}

const useStyles = makeStyles()((theme) => ({
    hiddenInPrint: {
        ...HIDDEN_IN_PRINT,
    },
    visibleOnlyInPrint: {
        ...VISIBLE_ONLY_IN_PRINT,
    },
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        justifyContent: "start",
        paddingBottom: "3.5rem",
        "@media print": {
            flex: "none",
            display: "block",
            backgroundColor: "transparent",
            margin: "0 auto",
            padding: 0,
            width: "100%",
            maxWidth: "190mm",
            minHeight: "auto",
            height: "auto",
            boxSizing: "border-box",
            overflow: "hidden",
            pageBreakInside: "auto",
            pageBreakAfter: "avoid",
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
    },
    header: {
        zIndex: 1,
        display: "grid",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.5rem",
        backgroundColor: "white",
        position: "sticky",
        top: 0,
        boxShadow: "0px 2px 6px 0px #00000014",
        gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", // L | C | R
        "@media print": {
            display: "none",
            padding: 0,
            height: 0,
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    "@media print": {
        "@page": {
            size: "A4",
            // top and bottom come from PrintPageHeaderFooter
            marginLeft: "10mm",
            marginRight: "10mm",
        },
        body: {
            margin: 0,
            padding: 0,
            width: "210mm",
            height: "297mm",
            boxSizing: "border-box",
        },
        "*": {
            display: "none",
        },
        ".slideContainer, .slideContainer *": {
            display: "block !important",
        },
    },
    noOrderContainer: {
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
    },
    noOrderTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 1,
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    chatPage: {
        width: "80%",
        padding: "3.5rem",
        alignSelf: "center",
        "@media print": {
            width: "100%",
            maxWidth: "210mm",
            padding: "1rem",
            margin: "0 auto",
            alignSelf: "stretch",
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            padding: 0,
        },
    },
    actionButtons: {
        justifySelf: "end",
    },
}))

type MockServiceCenterBooking = ServiceCenterBooking & { uid?: string } // To prevent ts errors for now as api does not return uid

export type AnyMeeting =
    | MeetingServiceOrder
    | MeetingServiceOrderAccounting
    | MenuMeeting
    | MeetingDetailsOrder
    | MockServiceCenterBooking
    | MeetingCardOrderTypes

export function hasDeliveryDetails(
    order: AnyMeeting,
): order is MeetingServiceOrder | MeetingServiceOrderAccounting | MenuMeeting | MeetingCardOrderTypes {
    return "deliveryDetails" in order
}

type MinimalMeeting<T extends { id: any; meetingDetails: { meetingName: any }; uid?: any }> = Pick<T, "id" | "uid"> & {
    meetingDetails: Pick<T["meetingDetails"], "meetingName">
}

export type AnyMinimalMeeting = MinimalMeeting<AnyMeeting>

type StartPageLayoutProps = Readonly<{
    id?: number
    activeMeeting?: AnyMeeting
    onClose?: () => void
    meetings?: AnyMeeting[]
    open?: boolean
    updateEdge?: string
    singleMode?: boolean
    renderOrderMenu?: RenderPrintViewOrderMenu
}>

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
const transformOrdersForExport = (data: MeetingServiceOrdersForExportResponse) => data?.orders

const getIndexOfPreviewedOrder = (orders?: AnyMinimalMeeting[], id?: number, activeOrder?: AnyMinimalMeeting) => {
    const activeIndex = activeOrder ? (orders?.indexOf(activeOrder) ?? -1) : -1
    if (activeIndex >= 0) return activeIndex
    if (!id || !orders?.length) return 0
    return orders.findIndex((o) => o.id == id)
}

const supportEmail = "support@facilitynet.dk"

export function UserMeetingsReceipt({
    id,
    activeMeeting,
    meetings = [],
    updateEdge,
    singleMode = true,
    open = false,
    onClose = () => {},
    renderOrderMenu,
}: StartPageLayoutProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { orderUid: uid } = useParams()
    const { currentEnv } = useEnvironment()
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const isAuthenticated = Boolean(userId)
    const enabledOrdersForExport = isAuthenticated && Boolean(meetings?.length) && !singleMode
    const merchant = enabledOrdersForExport ? useMerchant() : { merchantId: 0 }
    const isPublic = Boolean(uid)

    const [currentMeetingIndex, setCurrentMeetingIndex] = useState<number>(() =>
        getIndexOfPreviewedOrder(meetings, id, activeMeeting),
    )
    const activeOrder = useMemo(() => meetings?.[currentMeetingIndex], [meetings, currentMeetingIndex])
    const meetingIds = useMemo(() => meetings.map((order) => Number(order.id)), [meetings])
    const hasMeetingList = meetings.length > 0
    const activeOrderId = hasMeetingList ? Number(activeOrder?.id) || 0 : (id ?? 0)

    const { data: exportOrders = [], isFetching: isFetchingOrdersForExport } = useApiQuery(
        fetchMeetingServiceReceiptOrdersForExport,
        {
            queryName: MerchantMeetingServiceOrdersQuery.RECEIPT_ORDERS_FOR_EXPORT,
            dependencies: {
                merchantId: merchant?.merchantId ?? 0,
                orderIds: meetingIds,
            },
            enabled: enabledOrdersForExport,
            select: transformOrdersForExport,
        },
    )

    const { data: orderDetailsPublic, isLoading: orderDetailsPublicLoading } = usePublicApiQuery(
        fetchPublicOrderDetails,
        {
            queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS_PUBLIC,
            dependencies: {
                orderUid: uid ?? "",
            },
            enabled: Boolean(uid) && isPublic,
            select: transformData,
        },
    )

    const { data: orderDetailsAuth, isLoading: orderDetailsAuthLoading } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: activeOrderId,
        },
        enabled: Boolean(activeOrderId) && !isPublic,
        select: transformData,
    })

    const orderDetails = orderDetailsAuth ?? orderDetailsPublic
    const orderDetailsLoading = isPublic
        ? orderDetailsPublicLoading
        : orderDetailsAuthLoading || isFetchingOrdersForExport

    const receiptOrders = (singleMode ? [orderDetails] : exportOrders).flatMap((order) => (order ? [order] : []))
    const printedOrderIds = receiptOrders.map((order) => order.id)
    const { isPrintChatEnabled, isChatHistoryPrinted, printedSingleOrder, getOrderPrintActions, printWithChatHistory } =
        usePrintWithChatHistory(printedOrderIds)
    const printedOrders = receiptOrders.filter(
        (order) => !printedSingleOrder || order.id === printedSingleOrder.orderId,
    )

    const companyId = useAppSelector(selectEffectiveCompanyId)
    const effectiveUserType = useAppSelector(selectEffectiveUserType)
    const isMerchantViewer = effectiveUserType === LoginUserType.MERCHANT
    const [chatOrder, setChatOrder] = useState<ChatOrder>()
    const isChatVisible = isPrintChatEnabled && !isPublic
    const chatViewerRole = isMerchantViewer ? ChatMessageAuthorRole.MERCHANT : ChatMessageAuthorRole.CUSTOMER

    const skeletonCount = !singleMode ? meetings.length || 1 : 1

    const getListOrderMessages = (orderId: number) => {
        const listOrder = meetings.find((order) => order.id === orderId)
        return listOrder && hasDeliveryDetails(listOrder) && "messages" in listOrder ? listOrder.messages : undefined
    }

    const renderChatStatus = (orderId: number, title: string | undefined, hasChat?: boolean) =>
        isChatVisible && (
            <OrderChatStatusCard
                orderId={orderId}
                viewerRole={effectiveUserType === LoginUserType.SUPPORT ? undefined : chatViewerRole}
                hasChat={hasChat}
                orderMessages={getListOrderMessages(orderId)}
                onClick={() => setChatOrder({ id: orderId, title })}
            />
        )

    const renderReceiptContent = () => {
        if (orderDetailsLoading) {
            return (
                <Stack gap="4rem">
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <ReceiptDetailsSkeleton key={index} />
                    ))}
                </Stack>
            )
        }

        if (!orderDetails) {
            return (
                <Box className={classes.noOrderContainer}>
                    <Box className={classes.noOrderTitle}>
                        {isPublic ? (
                            <LocalizedStrict
                                id="user-order-receipt-page-not-found-public-message"
                                vars={{ supportEmail }}
                                elems={{ link: <Link to={`mailto:${supportEmail}`} /> }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Sorry, the order wasn&apos;t found. Please contact support at{" "}
                                    <link>{supportEmail}</link> if you need help.
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict
                                id="user-order-receipt-page-not-loaded-message"
                                vars={{ orderId: activeOrderId.toString() }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Order id {activeOrderId} could not be read. Please contact support.
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Box>
                </Box>
            )
        }

        if (singleMode) {
            return (
                <>
                    <ReceiptDetails
                        isFirst
                        orderDetails={orderDetails}
                        showContactDisclaimer={isMerchantViewer && !isPublic}
                        chatStatus={renderChatStatus(orderDetails.id, orderDetails.meetingDetails?.meetingName)}
                        orderMenu={renderOrderMenu?.(orderDetails.id)}
                    />
                    {isChatHistoryPrinted && (
                        <OrderChatHistoryPage className={classes.chatPage} orderId={orderDetails.id} />
                    )}
                </>
            )
        }

        const printedOrder = exportOrders.find((order) => order.id === printedSingleOrder?.orderId)

        return (
            <>
                <Stack gap="4rem" className={cx(printedOrder && classes.hiddenInPrint)}>
                    {exportOrders.map((order, index) => (
                        <PrintOrderPage key={order.id} orderId={order.id}>
                            <ReceiptDetails
                                isFirst={!index}
                                orderDetails={order}
                                showContactDisclaimer={isMerchantViewer && !isPublic}
                                chatStatus={renderChatStatus(
                                    order.id,
                                    order.meetingDetails?.meetingName,
                                    Boolean(order.messages?.length),
                                )}
                                orderMenu={renderOrderMenu?.(order.id, getOrderPrintActions(order.id))}
                            />
                            {isChatHistoryPrinted && (
                                <OrderChatHistoryPage className={classes.chatPage} orderId={order.id} />
                            )}
                        </PrintOrderPage>
                    ))}
                </Stack>
                {printedOrder && (
                    <Box className={classes.visibleOnlyInPrint}>
                        <ReceiptDetails isFirst orderDetails={printedOrder} />
                        {printedSingleOrder?.withChatHistory && (
                            <OrderChatHistoryPage className={classes.chatPage} orderId={printedOrder.id} />
                        )}
                    </Box>
                )}
            </>
        )
    }

    const goToPreviousMeeting = () => {
        if (currentMeetingIndex > 0) {
            const prevIndex = currentMeetingIndex - 1
            setCurrentMeetingIndex(prevIndex)
        }
    }

    const goToNextMeeting = () => {
        if (currentMeetingIndex < meetings.length - 1) {
            const nextIndex = currentMeetingIndex + 1
            setCurrentMeetingIndex(nextIndex)
        }
    }

    const content = (
        <Stack direction="column" spacing="2rem" className={classes.container}>
            <PrintPageHeaderFooter
                title={l10n.getString("receipt-title")}
                kitchen={getKitchenNames(printedOrders)}
                orderIds={printedOrders.map((order) => order.id)}
            />
            <Box className={classes.header}>
                <ReceiptAndSlipDialogHeader
                    selectedIndex={currentMeetingIndex}
                    onOrderChange={setCurrentMeetingIndex}
                    isAuthenticated={isAuthenticated}
                    isSelectMode={!singleMode}
                    orders={meetings}
                    orderIdsLength={meetings.length}
                    orderDetails={orderDetails}
                />
                <Link className={classes.logoLink} to={LOGIN_ROUTE}>
                    <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                </Link>

                <ReceiptNavigation
                    isAuthenticated={isAuthenticated}
                    isSelectMode={!singleMode}
                    goToPreviousOrder={goToPreviousMeeting}
                    goToNextOrder={goToNextMeeting}
                    ordersLength={meetings.length}
                    currentMeetingIndex={currentMeetingIndex}
                    variant="receipt"
                />

                <Box className={classes.actionButtons}>
                    <SharePrintButtons
                        onPrintWithChatHistory={isPublic ? undefined : printWithChatHistory}
                        onClose={onClose}
                        hasClose={!isPublic}
                        hasShare={singleMode && !!orderDetails?.uid}
                        copyUrl={`${currentEnv?.webapp?.baseUrl}${getPublicMeetingReceiptRoute(orderDetails?.uid)}`}
                    />
                </Box>
            </Box>

            {renderReceiptContent()}

            {chatOrder && (
                <OrderChatDrawer
                    open
                    raised
                    onClose={() => setChatOrder(undefined)}
                    orderId={chatOrder.id}
                    orderTitle={chatOrder.title}
                    viewerRole={chatViewerRole}
                    sender={getChatSender(effectiveUserType, userId, companyId)}
                />
            )}
        </Stack>
    )

    return isPublic ? (
        content
    ) : (
        <StandardDialog updateEdge={updateEdge} open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
